import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import './ArrSelectableColumnChart.scss';
import {Box, Typography} from '@mui/material';
import {ActionMenuProps} from '../../common/arr-action-button/ArrActionButton';
import {CustomMenuItem} from '../arr-menu/ArrMenu';
import {TIMELINE_BAR, TIMELINE_COLOR_MAP} from '../../../shared/constants';
import {t} from 'i18next';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export interface BarColumnsChartSelectableSeries {
  label: string;
  data: number[];
  color?: string;
}

export type TimelineType = 'yearly' | 'monthly' | 'daily';
export type SelectedBarInfoType = {
  timelineType: TimelineType;
  barKey: string;
  label: string;
  value: number;
  barIndex?: number;
};
export interface ArrColumnChartSelectableProps {
  labels: string[];
  series: BarColumnsChartSelectableSeries[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  title?: string;
  horizontal?: boolean;
  selectedBars?: string[];
  multiSelect?: boolean;
  showLegend?: boolean;
  gridDashColor?: string;
  gridDashPattern?: number[];
  barColor?: string;
  barActiveColor?: string;
  gridDashWidth?: number;
  height?: string;
  timelineType?: TimelineType;
  footerCaption?: string;
  onSelectionChange?: (selectedKeys: string[]) => void;
  onTimelineSelect?: (
    info: SelectedBarInfoType,
    isUnselected?: boolean
  ) => void;
  rightActionComponent?: React.ReactNode;
  actionMenuItems?: ActionMenuProps['menuItems'];
  onActionPick?: (item: CustomMenuItem) => void;
  dataTestId: string;
}

const useCssVar = (name: string, fallback: string) =>
  useMemo(
    () =>
      (typeof window !== 'undefined' &&
        getComputedStyle(document.documentElement)
          .getPropertyValue(name)
          .trim()) ||
      fallback,
    [name, fallback]
  );

const ArrSelectableColumnChart: React.FC<ArrColumnChartSelectableProps> = ({
  labels,
  series,
  xAxisLabel,
  title,
  horizontal = false,
  selectedBars = [],
  multiSelect = true,
  showLegend = false,
  height = TIMELINE_BAR.BarHeight,
  timelineType = 'yearly',
  onSelectionChange,
  onTimelineSelect,
  footerCaption,
  rightActionComponent,
  dataTestId,
}) => {
  const [internalSelectedBars, setInternalSelectedBars] =
    useState<string[]>(selectedBars);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [barWidth, setBarWidth] = useState(48);
  const timelineStringPath = 'reports.timelineAndGaps.';

  useEffect(() => {
    setInternalSelectedBars(selectedBars);
  }, []);

  useEffect(() => {
    onSelectionChange?.(internalSelectedBars);
  }, [internalSelectedBars]);

  const highlightGrey = useCssVar(
    '--highlight-grey',
    TIMELINE_COLOR_MAP.HighlightGrey
  );
  const defaultSeriesColor = useCssVar(
    '--data-data-05',
    TIMELINE_COLOR_MAP.DefaultSeries
  );
  const textBodyStrong = useCssVar(
    '--text-body-strong',
    TIMELINE_COLOR_MAP.TextBodyStrong
  );

  const timelineBrightColors: Record<TimelineType, string> = {
    yearly: TIMELINE_COLOR_MAP.Yearly,
    monthly: TIMELINE_COLOR_MAP.Monthly,
    daily: TIMELINE_COLOR_MAP.Daily,
  };

  const getBarKey = (seriesIdx: number, dataIdx: number) =>
    `${seriesIdx}-${dataIdx}`;
  const anySelection = internalSelectedBars.length > 0;

  const MIN_BAR_WIDTH = 36;
  const MAX_BAR_WIDTH = 120;
  const BAR_GAP = 15;
  const DAILY_MIN_BAR_WIDTH = 16;

  const totalBars = series.reduce((max, s) => Math.max(max, s.data.length), 0);

  useEffect(() => {
    if (!chartContainerRef.current || totalBars === 0) return;

    if (timelineType === 'daily') {
      setBarWidth(DAILY_MIN_BAR_WIDTH);
      return;
    }

    const containerWidth = chartContainerRef.current.offsetWidth;

    const availableWidthPerBar =
      (containerWidth - BAR_GAP * (totalBars - 1)) / totalBars;

    const calculatedWidth = Math.min(
      MAX_BAR_WIDTH,
      Math.max(MIN_BAR_WIDTH, availableWidthPerBar)
    );

    setBarWidth(calculatedWidth);
  }, [totalBars]);

  const calculatedChartWidth = totalBars * (barWidth + BAR_GAP);

  const datasets = series.map((s, seriesIdx) => ({
    label: s.label,
    data: s.data,
    backgroundColor: s.data.map((value, dataIdx) => {
      if (value === 0) return 'rgba(0,0,0,0)';
      const key = getBarKey(seriesIdx, dataIdx);
      const isSelected = internalSelectedBars.includes(key);

      if (anySelection) {
        return isSelected ? timelineBrightColors[timelineType] : highlightGrey;
      }
      return s.color || defaultSeriesColor;
    }),
    barThickness: barWidth,
    maxBarThickness: MAX_BAR_WIDTH,
    borderRadius: 0,
    borderWidth: 0,
  }));

  const processedLabels =
    timelineType === 'monthly'
      ? labels.map((label) => {
          const parts = label.split(' ');
          return parts.length === 2 ? parts : [label];
        })
      : labels;

  const chartData: ChartData<'bar'> = {
    labels: processedLabels,
    datasets,
  };

  const chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
      legend: {
        display: showLegend,
        labels: {color: textBodyStrong},
      },
    },
    scales: {
      x: {
        offset: true,
        title: {
          display: !!xAxisLabel,
          text: xAxisLabel,
          color: textBodyStrong,
          font: {size: 14, family: 'Inter'},
        },
        ticks: {
          font: {size: 12, lineHeight: 1.5, family: 'Inter', style: 'normal'},
          color: textBodyStrong,
        },
        grid: {display: false},
      },
      y: {
        title: {
          display: true,
          text: t(timelineStringPath + 'documentCount'),
          color: textBodyStrong,
          padding: {
            top: 15,
            bottom: 40,
          },
        },
        ticks: {
          color: textBodyStrong,
          callback: (value, index, ticks) =>
            index === 0 || index === ticks.length - 1 ? value : '',
        },
        border: {dash: [2, 4]},
        grid: {color: TIMELINE_COLOR_MAP.Grid},
      },
    },
    onClick: (event, elements) => {
      if (!elements || !elements.length) return;
      const el = elements[0];
      const seriesIdx = el.datasetIndex;
      const dataIdx = el.index;
      const key = getBarKey(seriesIdx, dataIdx);
      const updated = multiSelect
        ? internalSelectedBars.includes(key)
          ? internalSelectedBars.filter((k) => k !== key)
          : [...internalSelectedBars, key]
        : internalSelectedBars.includes(key)
          ? []
          : [key];
      const isUnselected = internalSelectedBars.includes(key);
      setInternalSelectedBars(updated);
      const barData = {
        label: labels[dataIdx],
        value: series[seriesIdx].data[dataIdx],
      };

      onTimelineSelect?.(
        {
          timelineType,
          barKey: key,
          label: barData.label,
          value: barData.value,
          barIndex: dataIdx,
        },
        isUnselected
      );
    },
  };

  const showDailyBottomLabel = timelineType === 'daily';

  return (
    <Box className="chart-wrapper">
      <Box className="timeline-header">
        <Typography
          className="typo-text subhead5_size"
          tabIndex={0}
          data-testid={`arr-selectable-column-title-${dataTestId}`}
        >
          {title}
        </Typography>
        {rightActionComponent}
      </Box>
      <div
        ref={chartContainerRef}
        className={`arr-column-chart-selectable ${
          showDailyBottomLabel ? 'space-bottom-lbl' : ''
        }`}
        style={{height, overflowX: 'auto'}}
        data-testid={`arr-selectable-column-chart-container-${dataTestId}`}
      >
        <div
          style={{minWidth: calculatedChartWidth, height: '100%'}}
          data-testid={`arr-selectable-column-chart-${dataTestId}`}
        >
          <Bar data={chartData} options={chartOptions} />
        </div>

        {showDailyBottomLabel && footerCaption && (
          <span
            className="bottom-label typo-text body4_size"
            style={{minWidth: calculatedChartWidth}}
            data-testid={`arr-selectable-column-footer-${dataTestId}`}
          >
            {footerCaption}
          </span>
        )}
      </div>
    </Box>
  );
};

export default ArrSelectableColumnChart;
